import { Box, Typography, Stack, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import { Checkbox, FormControlLabel } from "@mui/material";
import type { Control, FieldErrors } from "react-hook-form";
import type { CompanyGridForm } from "./CompanySettingsForm";
import { useTranslation } from "react-i18next";

type CompanyInformationSectionProps = {
    control: Control<CompanyGridForm>;
    errors: FieldErrors<CompanyGridForm>;
    isSupplierMode: boolean;
    alreadyHasOwner: boolean | undefined;
    platformOwnerChecked: boolean;
    setPlatformOwnerChecked: (checked: boolean) => void;
};

export const CompanyInformationSection = (props: CompanyInformationSectionProps) => {
    const { control, errors, isSupplierMode, alreadyHasOwner, platformOwnerChecked, setPlatformOwnerChecked } = props;
    const { t } = useTranslation();

    return (
        <Box>
            <Typography variant="h4" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "32px" }}>
                Information
            </Typography>
            <Stack spacing="24px">
                {/* Company name */}
                <Box sx={{ width: "275px" }}>
                    <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                        Company name* :
                    </Typography>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                {...field}
                                inputRef={ref}
                                placeholder="Enter company name"
                                variant="outlined"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "24px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E7E6E4",
                                        height: "40px",
                                        "& fieldset": {
                                            border: "none",
                                        },
                                        "& input": {
                                            padding: "0px 12px 0px 20px",
                                            fontWeight: 500,
                                            fontSize: "0.875rem",
                                            color: "#393A36",
                                            "&::placeholder": {
                                                color: "#A3A2A0",
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
                {!isSupplierMode && !alreadyHasOwner && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={platformOwnerChecked}
                                onChange={(e) => setPlatformOwnerChecked(e.target.checked)}
                            />
                        }
                        label={t("company.isPlatformOwner", "Platform owner")}
                    />
                )}

                {/* Two column layout for Registration Number and External ID */}
                <Box sx={{ display: "flex", gap: "24px" }}>
                    {/* Registration Number */}
                    <Box sx={{ width: "275px" }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            Registration Number* :
                        </Typography>
                        <Controller
                            name="registrationNumber"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter registration number"
                                    variant="outlined"
                                    error={Boolean(errors.registrationNumber)}
                                    helperText={errors.registrationNumber?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* External ID */}
                    <Box sx={{ width: "275px" }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            External ID* :
                        </Typography>
                        <Controller
                            name="externalId"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter external ID"
                                    variant="outlined"
                                    error={Boolean(errors.externalId)}
                                    helperText={errors.externalId?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};
